import { useState, useMemo, useCallback } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Badge,
  Button,
  Checkbox,
  Card,
  CardBody,
  EmptyState as DSEmptyState,
  Skeleton,
  Spinner,
} from '@kollektive/design-system';
import {
  Trash2,
  Database,
  AlertTriangle,
  Search,
  Zap,
  CheckCircle,
  XCircle,
  Pencil,
} from 'lucide-react';
import type { Connection, DatabaseDialect, TestConnectionResponse } from '@/types';
import styles from './ConnectionTable.module.css';

interface ConnectionTableProps {
  connections: Connection[];
  isLoading: boolean;
  error: Error | null;
  onRetry?: () => void;
  onEdit: (connection: Connection) => void;
  onDelete: (connection: Connection) => void;
  onBulkDelete: (connections: Connection[]) => void;
  onTest: (id: string) => Promise<TestConnectionResponse>;
  isDeleting?: boolean;
  searchQuery?: string;
}

type SortField = 'name' | 'dialect';
type SortDirection = 'asc' | 'desc';

const dialectConfig: Record<DatabaseDialect, { label: string }> = {
  postgres: {
    label: 'PostgreSQL',
  },
  mysql: {
    label: 'MySQL',
  },
  redshift: {
    label: 'Redshift',
  },
};

// Skeleton component for loading state
function TableSkeleton() {
  return (
    <Card>
      <CardBody className={styles.cardBody}>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader style={{ width: 40 }}><Skeleton width={16} height={16} /></TableHeader>
              <TableHeader>Name</TableHeader>
              <TableHeader>Type</TableHeader>
              <TableHeader>Read Only</TableHeader>
              <TableHeader>Active</TableHeader>
              <TableHeader>Timeout</TableHeader>
              <TableHeader>Max Rows</TableHeader>
              <TableHeader style={{ width: 120 }}></TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {[1, 2, 3].map((i) => (
              <TableRow key={i}>
                <TableCell><Skeleton width={16} height={16} /></TableCell>
                <TableCell><Skeleton width="8rem" height="1rem" /></TableCell>
                <TableCell><Skeleton width="5rem" height="1rem" /></TableCell>
                <TableCell><Skeleton width="3rem" height="1.25rem" /></TableCell>
                <TableCell><Skeleton width="3.5rem" height="1.25rem" /></TableCell>
                <TableCell><Skeleton width="2.5rem" height="1rem" /></TableCell>
                <TableCell><Skeleton width="3rem" height="1rem" /></TableCell>
                <TableCell><Skeleton width={60} height={24} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardBody>
    </Card>
  );
}

export function ConnectionTable({
  connections,
  isLoading,
  error,
  onRetry,
  onEdit,
  onDelete,
  onBulkDelete,
  onTest,
  isDeleting,
  searchQuery = '',
}: ConnectionTableProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<Record<string, TestConnectionResponse>>({});

  const handleTest = useCallback(async (e: React.MouseEvent, connectionId: string) => {
    e.stopPropagation();
    if (testingId) return;

    setTestingId(connectionId);
    // Clear previous result for this connection
    setTestResults((prev) => {
      const next = { ...prev };
      delete next[connectionId];
      return next;
    });

    try {
      const result = await onTest(connectionId);
      setTestResults((prev) => ({ ...prev, [connectionId]: result }));
      // Clear result after 5 seconds
      setTimeout(() => {
        setTestResults((prev) => {
          const next = { ...prev };
          delete next[connectionId];
          return next;
        });
      }, 5000);
    } catch (err) {
      const errorResult: TestConnectionResponse = {
        success: false,
        error: err instanceof Error ? err.message : 'Test failed',
      };
      setTestResults((prev) => ({ ...prev, [connectionId]: errorResult }));
      setTimeout(() => {
        setTestResults((prev) => {
          const next = { ...prev };
          delete next[connectionId];
          return next;
        });
      }, 5000);
    } finally {
      setTestingId(null);
    }
  }, [testingId, onTest]);

  // Filter connections by search query
  const filteredConnections = useMemo(() => {
    if (!searchQuery.trim()) return connections;

    const query = searchQuery.toLowerCase();
    return connections.filter((conn) =>
      conn.name.toLowerCase().includes(query) ||
      conn.host.toLowerCase().includes(query) ||
      conn.database_name.toLowerCase().includes(query) ||
      conn.dialect.toLowerCase().includes(query) ||
      conn.description?.toLowerCase().includes(query)
    );
  }, [connections, searchQuery]);

  // Sort filtered connections
  const sortedConnections = useMemo(() => {
    return [...filteredConnections].sort((a, b) => {
      let aVal: string;
      let bVal: string;

      switch (sortField) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'dialect':
          aVal = a.dialect;
          bVal = b.dialect;
          break;
        default:
          return 0;
      }

      return sortDirection === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    });
  }, [filteredConnections, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(sortedConnections.map((c) => c.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (connectionId: string, checked: boolean) => {
    const newSet = new Set(selectedIds);
    if (checked) {
      newSet.add(connectionId);
    } else {
      newSet.delete(connectionId);
    }
    setSelectedIds(newSet);
  };

  const handleBulkDelete = () => {
    const connectionsToDelete = connections.filter((c) => selectedIds.has(c.id));
    onBulkDelete(connectionsToDelete);
    setSelectedIds(new Set());
  };

  const allSelected = sortedConnections.length > 0 && selectedIds.size === sortedConnections.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < sortedConnections.length;

  if (isLoading) {
    return <TableSkeleton />;
  }

  if (error) {
    return (
      <DSEmptyState
        variant="error"
        icon={<AlertTriangle size={48} />}
        title="Failed to load connections"
        description={error.message}
        action={onRetry ? <Button onClick={onRetry}>Try again</Button> : undefined}
      />
    );
  }

  if (connections.length === 0) {
    return (
      <DSEmptyState
        icon={<Database size={48} />}
        title="No connections yet"
        description="Add your first database connection to enable AI-powered queries."
      />
    );
  }

  return (
    <div className={styles.container}>
      {/* Table in Card */}
      {filteredConnections.length > 0 ? (
        <Card className={styles.tableCard}>
          <CardBody className={styles.cardBody}>
            <Table hoverable>
              <TableHead>
                <TableRow>
                  <TableHeader style={{ width: 40 }} className={styles.checkboxCell}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </TableHeader>
                  <TableHeader
                    sortable
                    sortDirection={sortField === 'name' ? sortDirection : null}
                    onClick={() => handleSort('name')}
                  >
                    Name
                  </TableHeader>
                  <TableHeader
                    sortable
                    sortDirection={sortField === 'dialect' ? sortDirection : null}
                    onClick={() => handleSort('dialect')}
                  >
                    Type
                  </TableHeader>
                  <TableHeader>
                    Read Only
                  </TableHeader>
                  <TableHeader>
                    Active
                  </TableHeader>
                  <TableHeader>
                    Timeout
                  </TableHeader>
                  <TableHeader>
                    Max Rows
                  </TableHeader>
                  <TableHeader style={{ width: 140 }}></TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedConnections.map((connection) => {
                  const config = dialectConfig[connection.dialect] || dialectConfig.postgres;
                  const testResult = testResults[connection.id];
                  const isTesting = testingId === connection.id;

                  return (
                    <TableRow
                      key={connection.id}
                      selected={selectedIds.has(connection.id)}
                      onClick={() => onEdit(connection)}
                      style={{ cursor: 'pointer' }}
                    >
                      <TableCell className={styles.checkboxCell} onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.has(connection.id)}
                          onChange={(e) => handleSelectOne(connection.id, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className={styles.nameCell}>
                          <span className={styles.connectionName}>{connection.name}</span>
                          {connection.description && (
                            <span className={styles.connectionDescription}>{connection.description}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{config.label}</TableCell>
                      <TableCell>
                        <Badge variant={connection.read_only ? 'warning' : 'neutral'} styleVariant="subtle" size="sm">
                          {connection.read_only ? 'Yes' : 'No'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={connection.is_active ? 'success' : 'neutral'} styleVariant="subtle" size="sm">
                          {connection.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {connection.query_timeout_seconds ? `${connection.query_timeout_seconds}s` : '—'}
                      </TableCell>
                      <TableCell>
                        {connection.max_rows?.toLocaleString() || '—'}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className={styles.actions}>
                          {testResult ? (
                            <div className={testResult.success ? styles.testSuccess : styles.testError}>
                              {testResult.success ? (
                                <CheckCircle size={14} />
                              ) : (
                                <XCircle size={14} />
                              )}
                            </div>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => handleTest(e, connection.id)}
                              disabled={isTesting}
                              title="Test connection"
                              className={styles.actionButton}
                            >
                              {isTesting ? <Spinner size="sm" /> : <Zap size={16} />}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(connection);
                            }}
                            title="Edit connection"
                            className={styles.actionButton}
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(connection);
                            }}
                            disabled={isDeleting}
                            title="Delete connection"
                            className={styles.deleteButton}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardBody>
        </Card>
      ) : (
        <Card className={styles.tableCard}>
          <CardBody>
            <DSEmptyState
              variant="search"
              icon={<Search size={48} />}
              title="No results"
              description={`No connections match "${searchQuery}"`}
            />
          </CardBody>
        </Card>
      )}

      {/* Bulk actions bar at bottom - always rendered to prevent layout shift */}
      <div className={`${styles.bulkActions} ${selectedIds.size === 0 ? styles.bulkActionsHidden : ''}`}>
        <span className={styles.selectedCount}>
          {selectedIds.size} selected
        </span>
        <Button
          variant="danger"
          size="sm"
          onClick={handleBulkDelete}
          disabled={isDeleting}
          iconLeft={<Trash2 size={16} />}
        >
          Delete Selected
        </Button>
      </div>
    </div>
  );
}
